<template>
    <v-container>

        <v-row>
            
            <v-col cols="12">
                <v-card>
                    <v-row >
                        <v-col cols="12">
                            <h2 class="text-center">{{title}}</h2>
                        </v-col>
                    </v-row>

                    <v-row  class="search-container">

                        <v-col cols="12" sm="6" md="2">
                            <v-menu
                                v-model="showSearchStartDate"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                max-width="290px"
                                min-width="290px"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        :value="search.startDate ? $moment(search.startDate, $constants.DATE_PICKER_FORMAT).format($constants.DATE_FORMAT_NO_TIME) : null"
                                        label="Start Date"
                                        readonly
                                        prepend-icon="event"
                                        append-icon="close"
                                        @click:append="searchStartDateChanged(null)"
                                        v-on="on"
                                    >
                                    </v-text-field>
                                </template>
                                <v-date-picker
                                    v-if="showSearchStartDate"
                                    :value="search.startDate"
                                    @input="searchStartDateChanged($event)">
                                </v-date-picker>
                            </v-menu>
                        </v-col>

                        <v-col cols="12" sm="6" md="2">
                            <v-menu
                                v-model="showSearchEndDate"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                max-width="290px"
                                min-width="290px"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        :value="search.endDate ? $moment(search.endDate, $constants.DATE_PICKER_FORMAT).format($constants.DATE_FORMAT_NO_TIME) : null"
                                        label="End Date"
                                        readonly
                                        prepend-icon="event"
                                        append-icon="close"
                                        @click:append="searchEndDateChanged(null)"
                                        v-on="on"
                                    >
                                    </v-text-field>
                                </template>
                                <v-date-picker
                                    v-if="showSearchEndDate"
                                    :value="search.endDate"
                                    @input="searchEndDateChanged($event)">
                                </v-date-picker>
                            </v-menu>
                        </v-col>

                        <v-col cols="9" sm="6" md="2">
                            <v-select
                                :items="types"
                                item-text="TypeName"
                                item-value="TypeValue"
                                v-model="search.type"
                                label="Type"
                            ></v-select>
                        </v-col>

                        <v-col cols="9" sm="6" md="2">
                            <v-select
                                :items="species"
                                item-text="SpeciesFullName"
                                item-value="SpeciesID"
                                v-model="search.speciesIDs"
                                label="Species"
                                clearable
                                multiple
                            ></v-select>
                        </v-col>

                        <v-col cols="9" sm="6" md="2">
                            <v-checkbox
                                label="Open Species Batch Pools Only"
                                v-model="search.openOnly"
                            ></v-checkbox>
                        </v-col>

                        <v-col cols="12" sm="6" md="2">
                            <v-checkbox
                                label="Inactive Pools Only"
                                v-model="search.inactiveOnly"
                            ></v-checkbox>
                        </v-col>

                        <v-col cols="12" sm="6" md="3" offset-md="3">
                            <v-btn block color="primary" :loading="loadingTableData" @click="loadTableData">Search</v-btn>
                        </v-col>

                        <v-col cols="12" sm="12" md="3">
                            <v-btn
                                v-if="!$auth.currentUserIsReadOnly()"
                                color="success"
                                block
                                @click="addNewSpeciesBatchPool()"
                            >
                                New Species Batch Pool
                            </v-btn>
                        </v-col>

                    </v-row>

                    <v-row >
                        <v-col cols="12">
                            <v-alert v-if="activePoolsError" outlined :value="true" color="error" icon="error">
                                {{activePoolsError}}
                            </v-alert>
                            <v-data-table
                                :headers="headers"
                                :items="tableData"
                                :loading="loadingTableData"
                                :footer-props="footerProps"
                                :server-items-length="totalTableDataCount"
                                :sort-by.sync="pagination.sortBy"
                                :sort-desc.sync="pagination.descending"
                                :items-per-page.sync="pagination.rowsPerPage"
                                :page.sync="pagination.page"
                                must-sort
                            >
                                <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
                                <template  v-slot:item="props">
                                    <tr :data-id="props.item.SpeciesBatchPoolID">
                                        <td>{{props.item.Barcode.toUpperCase()}}</td>
                                        <td>{{$moment.utc(props.item.CreatedTime).local().format($constants.DATE_FORMAT)}}</td>
                                        <td>{{props.item.CreatedByFullName}} ({{props.item.CreatedByEmail}})</td>
                                        <td>{{props.item.SpeciesFullName}}</td>
                                        <td>{{props.item.IsExternal ? 'External' : 'Internal'}}</td>
                                        <td>{{props.item.TotalWeight || 0}}g</td>
                                        <td>{{props.item.TotalRemainingWeight || 0}}g</td>
                                        <td>{{props.item.Status}}</td>
                                        <td>
                                            <v-btn
                                                block
                                                :color="(props.item.IsClosed ? 'info' : 'accent')"
                                                @click="viewRecord(props.item.SpeciesBatchPoolID)"
                                                v-if="!props.item.Inactive"
                                            >
                                                {{(props.item.IsClosed ? 'View' : 'Continue')}}
                                            </v-btn>
                                        </td>
                                        <td>
                                            <v-btn
                                                color="info"
                                                block
                                                @click="printBarcode(props.item.Barcode)"
                                                v-if="!props.item.Inactive"
                                            >
                                                Print Barcode
                                            </v-btn>
                                        </td>
                                        <td>
                                            <v-btn
                                                class="ml-2"
                                                color="success"
                                                @click="inactivePool(props.item)"
                                                block
                                                v-if="!props.item.Inactive&&!props.item.TotalWeight&&permission"
                                            >
                                                Inactivate Pool
                                            </v-btn>
                                            <v-btn
                                                class="ml-2"
                                                color="success"
                                                :loading="activingPools "
                                                @click="activePool(props.item)"
                                                block
                                                v-if="props.item.Inactive&&permission"
                                            >
                                                Activate Pool
                                            </v-btn>
                                        </td>
                                    </tr>
                                </template>
                                <template v-if="!loadingTableData" slot="no-data">
                                    <v-alert v-if="loadTableDataError" outlined :value="true" color="error" icon="error">
                                        {{loadTableDataError}}
                                    </v-alert>
                                    <v-alert v-if="!loadTableDataError" outlined :value="true" type="info">
                                        No results found
                                    </v-alert>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>

                </v-card>
            </v-col>

        </v-row>
        <SpeciesBatchPoolInactiveDialog ref="inactiveDialog" @updated="loadTableData"> </SpeciesBatchPoolInactiveDialog>
    </v-container>
</template>

<script>

import { mapState } from 'vuex';
import { mapGetters } from 'vuex';
import moment from 'moment';

import util from '@/helpers/util';
import constants from 'comunet-btg-utah-repository-shared/constants';

import * as VenomPoolingAPI from "@/services/venomPoolingAPI";

import SpeciesBatchPoolInactiveDialog from "./SpeciesBatchPoolInactiveDialog.vue"

export default {
    components: {
        SpeciesBatchPoolInactiveDialog,
    },
    props: [
        
    ],
    data() {
        return {
            search: {
                speciesIDs: [],
                inactiveOnly:false,
                openOnly: false,
                startDate: null,
                endDate: null,
                type: null
            },

            pagination: {
                rowsPerPage: 20, // default
                sortBy: "CreatedTime",
                descending: true,
                page: 1
            },

            showSearchStartDate: false,
            showSearchEndDate: false,

            activingPools:false,
            activePoolsError:null,
    
            loadingTableData: false,
            loadTableDataError: null,
            tableData: [],
            totalTableDataCount: 0,

            headers: [
                {
                    text: "ID",
                    value: "Barcode"
                },
                {
                    text: "Created Time",
                    value: "CreatedTime"
                },
                {
                    text: "Created By",
                    value: "CreatedByEmail"
                },
                {
                    text: "Species",
                    value: "SpeciesFullName"
                },
                {
                    text: "Type",
                    value: "IsExternal"
                },
                {
                    text: "Total Weight",
                    value: "TotalWeight"
                },
                {
                    text: "Remaining Weight",
                    value: "TotalRemainingWeight"
                },
                {
                    text: "Status",
                    value: "Status"
                },
                {
                    text: "",
                    sortable: false
                },
                {
                    text: "",
                    sortable: false
                },
                {
                    text: "",
                    sortable: false
                }
            ],

            footerProps: {
                'items-per-page-options': [5, 20, 50, 100]
            },

            types: [
                {
                    TypeName: "All",
                    TypeValue: null
                },
                {
                    TypeName: "Internal",
                    TypeValue: 1
                },
                {
                    TypeName: "External",
                    TypeValue: 2
                }
            ]
        };
    },
    computed: {
        title: function() {
            return this.$route.meta.title;
        },
        species() {
            return this.$store.state.speciesStore.species.filter(s => s.Active).sort(util.sortByField("SpeciesFullName"));
        },
        ...mapState("speciesStore", {
            loadingSpecies: state => state.loading
        }),
        permission:function(){
            return this.$store.state.authStore.user.UserProfile.Permissions.admin;
        },
    },
    methods: {
        viewRecord(speciesBatchPoolID) {
            this.$router.push({ name: 'speciesBatchPool', params: { speciesBatchPoolID: speciesBatchPoolID }});
        },
        searchStartDateChanged(newValue) {
            this.search.startDate = newValue;

            this.showSearchStartDate = false;
        },
        searchEndDateChanged(newValue) {
            this.search.endDate = newValue;

            this.showSearchEndDate = false;
        },
        printBarcode(barcode) {
            let routeData = this.$router.resolve({name: 'printbarcode', params: { barcode: barcode }});
            window.open(routeData.href, '_blank');
        },
        async loadTableData() {
            try {
                this.loadingTableData = true;
                this.loadTableDataError = null;

                const params = {
                    ...this.search,
                    ...this.pagination
                };

                if (params.startDate) {
                    params.startDate = moment(params.startDate).utc().format(constants.MYSQL_DATE_FORMAT_WITH_TIME);
                }
                
                if (params.endDate) {
                    params.endDate = moment(params.endDate).utc().format(constants.MYSQL_DATE_FORMAT_WITH_TIME);
                }

                //const data = await VenomPoolingAPI.getVenomLots(params);
                const data = await VenomPoolingAPI.getSpeciesBatchPools(params);

                this.totalTableDataCount = data.totalCount;
                this.tableData = data.data;
            }
            catch (e) {
                this.tableData = [];
                this.loadTableDataError = e;

                // eslint-disable-next-line no-console
                console.log(e);
            }
            finally {
                this.loadingTableData = false;
            }
        },
        addNewSpeciesBatchPool() {
            this.$router.push({ name: 'speciesBatchPool'});
        },
        inactivePool(currentPool) {
            this.$refs.inactiveDialog.showDialog(currentPool.SpeciesBatchPoolID);
        },
        async activePool(currentPool) {

            this.activingPools = true;
            try {
                var inactivePoolRequest = {
                    SpeciesBatchPoolID: currentPool.SpeciesBatchPoolID,
                    Inactive: false,
                };

                await VenomPoolingAPI.createUpdateSpeciesBatchPool(inactivePoolRequest);
            
                this.$emit("updated");

            }
            catch (e) {
                this.activePoolsError = e;
            }
            finally{
                this.activingPools = false;
                this.loadTableData();
            }

        },
    },
    async created() {
        this.$store.dispatch("speciesStore/loadSpecies");

        await this.loadTableData();
    },
    watch: {
        pagination: {
            async handler() {
                await this.loadTableData();
            },
            deep: true
        }
    },
};
</script>

<style lang="scss" scoped>
    #app {
        
    }
</style>